import React, { useMemo } from "react";
import { Field, Select } from "@introist/react-foundation/v2";
import { prefixForm } from "utils/FormUtils";
import { useDataSources } from "modules/datasources/hooks/useDataSources";
import { StepEditorAccordion } from "modules/workflows/routes/WorkflowEditor/StepEditorAccordion/StepEditorAccordion";
import { TagAwareInput } from "modules/flows/components/TagAwareInput";
import { DataSourceIcon } from "modules/datasources/components/DataSourceIcon";
import { StepEditorProps } from "../useStepEditor";
import { Block } from "components/atoms";

export const HibobWorkEntryStepEditor = ({ form, stepDataPath, workflowId }: StepEditorProps) => {
  const prefixedForm = prefixForm(form, stepDataPath);
  const { dataSources } = useDataSources();

  // Build HiBob datasource options
  const hibobSourceOptions = useMemo(() => {
    if (!dataSources) return [];
    return dataSources
      .filter(ds => ds.sourceType === "hibob")
      .map(s => ({
        key: s.id,
        title: s.name,
        startAdornment: <DataSourceIcon dataSource="hibob" size="small" />
      }));
  }, [dataSources]);

  return (
    <>
      {/* Connection Section */}
      <Block blended compact>
        <StepEditorAccordion title="Connection" defaultOpen>
          <StepEditorAccordion.Row label="HiBob Datasource">
            <Select
              size="small"
              element="button"
              variant="blended"
              style={{ width: "100%" }}
              options={hibobSourceOptions}
              value={prefixedForm.get("datasourceId")}
              onSelect={opt => prefixedForm.set("datasourceId")(opt.key)}
              placeholder="Select HiBob integration"
              attachToRef={false}
              error={prefixedForm.isError("datasourceId")}
            />
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>

      {/* Work Entry Details Section */}
      <Block blended compact>
        <StepEditorAccordion title="Work Entry Details" defaultOpen>
          <StepEditorAccordion.Row label="Effective Date">
            <Field>
              <TagAwareInput
                value={prefixedForm.get("effectiveDate")}
                onChange={prefixedForm.set("effectiveDate")}
                error={prefixedForm.isError("effectiveDate")}
                placeholder="{{employee.startDate}} or 2024-01-31"
                size="small"
                variant="blended"
                disabled={!prefixedForm.get("datasourceId")}
                tagSelectContext={{ workflowId }}
              />
            </Field>
          </StepEditorAccordion.Row>

          <StepEditorAccordion.Row label="Reason for Change">
            <Field>
              <TagAwareInput
                value={prefixedForm.get("reason") || ""}
                onChange={prefixedForm.set("reason")}
                placeholder="Promotion or {{fields.changeReason}}"
                size="small"
                variant="blended"
                disabled={!prefixedForm.get("datasourceId")}
                error={prefixedForm.isError("reason")}
                tagSelectContext={{ workflowId }}
              />
            </Field>
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>

      {/* Optional Fields Section */}
      <Block blended compact>
        <StepEditorAccordion title="Optional Updates" defaultOpen>
          <StepEditorAccordion.Row label="Department">
            <Field>
              <TagAwareInput
                value={prefixedForm.get("department")}
                onChange={prefixedForm.set("department")}
                error={prefixedForm.isError("department")}
                placeholder="Engineering or {{fields.newDepartment}}"
                size="small"
                variant="blended"
                disabled={!prefixedForm.get("datasourceId")}
                tagSelectContext={{ workflowId }}
              />
            </Field>
          </StepEditorAccordion.Row>

          <StepEditorAccordion.Row label="Reports To">
            <Field>
              <TagAwareInput
                value={prefixedForm.get("reportsTo")}
                onChange={prefixedForm.set("reportsTo")}
                error={prefixedForm.isError("reportsTo")}
                placeholder="{{fields.newManagerId}} or employee ID"
                size="small"
                variant="blended"
                disabled={!prefixedForm.get("datasourceId")}
                tagSelectContext={{ workflowId }}
              />
            </Field>
          </StepEditorAccordion.Row>
        </StepEditorAccordion>
      </Block>
    </>
  );
};
